import React from 'react'
import '../../../assets/scss/components/OnboardingGuideBanner/onboarding-guide-banner.css'
import { NavigationButton } from '../../Shared'
import { teacher_selfStartPath, teacher_studentStartPath } from './data'
import { OnboardingTeacherContext } from '../../../contexts/OnboardingTeacherContextProvider'
import { useOnboardingUtils } from '../useOnboardingUtils'
import { status,step_id } from '../onboardingUtils'
import _ from 'lodash'

/*This component is responsible for displaying onboarding guide banner.
 Should be shown on every page with the following conditions:
  - [x] If all guide tasks are completed, should not show
  - [x] If teacher_self guide is last completed, show teacher_self guide 
        (same for teacher_student guide)
  - [x] If teacher_self guide is completed, but teacher_student guide is 
        not, show teacher_self guide
  - [X] Should adjust heights on smaller screens and expanded sidebar

 Data endpoints/links:
  - [x] should only update its status based on OnboardingTeacherContext
  - [X] links/data should come only from associated data.js
*/
const OnboardingGuideBanner = () => {
    const {teacherStartTrack, studentStartTrack, currentPath} = React.useContext(OnboardingTeacherContext)
    const {handleTaskSkip} = useOnboardingUtils()
//the following checks on which path is currently ongoing
    let data, statusContext
    if(currentPath === 'teacher_selfStartPath'){
        data = teacher_selfStartPath
        statusContext = teacherStartTrack
    }
    else{
        data = teacher_studentStartPath
        statusContext = studentStartTrack
    }

//I don't particularly like this implementation, but it is working, will change later when better methods are thought out.
//toggle className based on current width of banner 
    const [bannerClassName,setBannerClassName] = React.useState('OnboardingGuideBanner')
    //Observing width change and trigger style change 
    const resizeObserver = React.useRef(new ResizeObserver((entries) => {
        const width = entries[0].borderBoxSize[0].inlineSize
        if(width <= 850){
            setBannerClassName('OnboardingGuideBanner-small')
        }
        else{
            setBannerClassName('OnboardingGuideBanner')
        }
    }))
   
    const resizeOnboardingBannerRef = React.useCallback((container)=>{
        if(container !== null){
            resizeObserver.current.observe(container)
        }
        else{
            if(resizeObserver.current){
                resizeObserver.current.disconnect()
            }
        }
    },[resizeObserver.current])
  
    // triangle style props
    const triangleStyle = (status) => {

        const colorCode = {
            'disabled':'#EBECEF', 
            'skipped':'white', 
            'inProgress':'white', 
            'completed':'#D7F9C8', 
            'advancedCompletion':'#EBECEF', 
        }

        return {
            borderSize:bannerClassName === 'OnboardingGuideBanner'? 72 : 100,
            height:bannerClassName === 'OnboardingGuideBanner'? 92 : 120,
            size:46,
            color:colorCode[status],
            backgroundColor:'transparent'
        }
    }
    
    return(
        <div data-testid='OnboardingGuideBanner' className={`${bannerClassName} no-print`} ref={resizeOnboardingBannerRef}>
            <ul>
                {step_id.map((content,index)=>{
                    return (
                    (currentPath === 'teacher_selfStartPath' && index < _.size(data)) || 
                    (currentPath === 'teacher_studentStartPath' && index >= _.size(teacher_selfStartPath))?
                    (
                        <li 
                         className={`OnboardingGuideBanner-step `+ `${statusContext.steps[data[content].contextValue] === _.indexOf(status,'inProgress')?'_active':'_inactive'}`} 
                         key={data[content].mainTitle+index}
                        >
                            <div className={`OnboardingGuideBanner-step-content ` + 
                                 `${statusContext.steps[data[content].contextValue] === _.indexOf(status,'completed')? 'completed' : 
                                 [_.indexOf(status,'disabled'),_.indexOf(status,'advancedCompletion')].includes(statusContext.steps[data[content].contextValue])? 'disabled' : ''}`}
                            >
                                <div className='OnboardingGuideBanner-ListHead'>
                                    <div className={`OnboardingGuideBanner-ListHead-count ${statusContext.steps[data[content].contextValue] === _.indexOf(status,'advancedCompletion')? 'completed':''}`}>
                                        {currentPath === 'teacher_selfStartPath'? 
                                            index + 1: 
                                            index + 1 - _.size(teacher_selfStartPath)
                                        }
                                    </div>
                                    {statusContext.steps[data[content].contextValue] === _.indexOf(status,'inProgress')?
                                    <React.Fragment>
                                        <p>{data[content].mainTitle}</p>
                                        <div className='OnboardingGuideBanner-ListHead-actions'>
                                        {data[content].actions?.map((action, action_index)=>{
                                            return <NavigationButton 
                                                message={action.text}
                                                styleType={`${action.style}_small`}
                                                actionType = {action.actionType}
                                                actionFunction = {() => {action.actionType === 'button' && handleTaskSkip(index)}}
                                                url={action.actionType === 'link' && action.url} 
                                                isExternal={action.isExternal} 
                                                key={data[content].mainTitle+ action_index}
                                            />
                                                
                                        })}
                                        </div>
                                    </React.Fragment>
                                    :null }
                                    
                                </div>
                                {statusContext.steps[data[content].contextValue] === _.indexOf(status,'inProgress') && <p className='OnboardingGuideBanner-step-text'>{data[content].mainText}</p>}
                            </div>
                            { 
                                (currentPath === 'teacher_selfStartPath' && index +1 < _.size(data)) || 
                                (currentPath === 'teacher_studentStartPath' && index+1 < step_id.length)?
                                <DoubleTriangle
                                    style={statusContext.steps[data[content].contextValue] !== undefined ? 
                                        triangleStyle(status[statusContext.steps[data[content].contextValue]]) : 
                                        triangleStyle(1)
                                     }
                                />:null
                            }
                        </li>
                    ):null)
                })}
                
            </ul>
        </div>
    )
}

//consider extracting these triangles out to shared components later...
const DoubleTriangle = ({style}) =>{
    const triangleContainer = {
        height:`${style.height}px`,
        width:'32px',
        overflow:'hidden',
        position:'absolute',
        display:'flex',
        alignItems:'center',
        justifyContent:'center',
        right:'-32px',
        zIndex:'10'
    }

    const triangle = {
        borderTop:`${style.borderSize}px solid transparent`,
        borderBottom:`${style.borderSize}px solid transparent`,
        borderLeft:`${style.size}px solid ${style.color}`,
        position:'absolute',
        left:'-16px',
        zIndex:'1'
    }

    const triangleBorder = {
        borderTop:`${style.borderSize}px solid transparent`,
        borderBottom:`${style.borderSize}px solid transparent`,
        borderLeft:`${style.size}px solid #B1B5BE`,

        position:'absolute',
        left:'-15px',
        zIndex:'0'
    }


    return (
    <div style={triangleContainer}>
        <div style={triangle}>
        </div>
        <div style={triangleBorder}>
        </div>
    </div>)
}
export default OnboardingGuideBanner